import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  defaultDataIdFromObject,
  ApolloLink,
} from '@apollo/client'
import apolloLogger from 'apollo-link-logger'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
// eslint-disable-next-line import/no-extraneous-dependencies
import { onError } from '@apollo/client/link/error'
import { AppError, getTokens } from '@landscape/ui-lib'

const {
  VITE_REACT_APP_API_URL = '',
  VITE_NODE_ENV = 'development',
  VITE_REACT_APP_CB_ENV = 'palm',
} = import.meta.env

const url = VITE_REACT_APP_API_URL ? VITE_REACT_APP_API_URL.replace(/"/g, '') : ''
const graphqlEndpoint = `${url}/graphql`

let client: any

const getClient = () => {
  if (client) {
    return client
  }

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      )
    }

    if (networkError) {
      console.error('[Network error]:', networkError)
    }
  })

  const authMiddleware = new ApolloLink((operation: any, forward: any) => {
    const { jwt, authenToken, authorToken } = getTokens()

    if (isEmpty(authenToken)) {
      throw new AppError({ code: 'missing_auth_info', message: 'Missing x-auth-token' })
    }

    if (isEmpty(authorToken)) {
      throw new AppError({
        code: 'missing_auth_info',
        message: 'Missing X-CB-Catapult-Authentication-Token',
      })
    }

    if (isEmpty(jwt)) {
      throw new AppError({
        code: 'missing_auth_info',
        message: 'Missing X-CB-Catapult-Authorization-Token',
      })
    }

    operation.setContext({
      headers: {
        'x-auth-token': authenToken,
        'X-CB-Catapult-Authorization-Token': jwt,
        'X-CB-Catapult-Authentication-Token': authorToken,
      },
    })

    return forward(operation)
  })

  const cache = new InMemoryCache({
    dataIdFromObject: (obj: any) => {
      // eslint-disable-next-line no-underscore-dangle
      switch (obj.__typename) {
        case 'DiType':
          return obj.di_code
        case 'AuditType':
          return `${obj.di_code}-${obj.username}`
        default:
          return defaultDataIdFromObject(obj) // fall back to default handling
      }
    },
  })

  const buildFetchURL = (baseURI: string, params: any) => {
    let fetchURL = baseURI + '?'

    Object.keys(params).forEach((key) => {
      if (!fetchURL.endsWith('?')) {
        fetchURL += '&'
      }

      const value = params[key] || ''
      if (isEmpty(value)) {
        fetchURL += key
      } else {
        fetchURL += `${key}=${value}`
      }
    })

    return fetchURL
  }

  const customFetch = (uri: string, options: any) => {
    const body = JSON.parse(options.body)
    const operationName = get(body, 'operationName') // graphql operation name

    const orgId = client.orgId

    const fetchURL = buildFetchURL(uri, {
      operationName,
      cbEnv: VITE_REACT_APP_CB_ENV,
      orgId,
      connectionId: sessionStorage.getItem('connectionId'),
    })

    return fetch(fetchURL, options)
  }

  const headers: any = {
    // 'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=-1,private',
    Pragma: 'no-cache',
    // Expires: 0,
    // 'Access-Control-Allow-Origin': '*',
    // 'X-Frame-Options': 'SAMEORIGIN',
    'Cache-Control': 'no-cache, must-revalidate',
    'Strict-Transport-Security': 'max-age=63072000',
    'X-XSS-Protection': '1; mode=block',
    'X-Content-Type-Options': 'nosniff',
    // 'Content-Security-Policy': 'frame-ancestors self',
  }

  // const apiKey = import.meta.env.REACT_APP_API_KEY
  // if (!isEmpty(apiKey)) {
  //   headers['x-api-key'] = apiKey
  // }

  const httpLinkOptions: any = {
    uri: graphqlEndpoint,
    fetch: customFetch,
    // credentials: 'include',
  }

  httpLinkOptions.headers = headers

  const httpLink = new HttpLink({ ...httpLinkOptions })

  let linkList = [errorLink, authMiddleware, httpLink]

  if (VITE_NODE_ENV !== 'production') {
    window.apolloCache = cache
    linkList.unshift(apolloLogger)
  }

  const apolloLink = ApolloLink.from(linkList)

  client = new ApolloClient({
    link: apolloLink,
    cache,
  })

  return client
}

export { getClient }
